import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PackageOpen, Sprout, Wheat } from 'lucide-react';
import { Reveal } from './Reveal';
import { Section } from './Section';
import styles from './StepsSection.module.css';

const steps = [
  {
    day: 'Day 1',
    label: 'You Open the Kit',
    desc: 'Five minutes. Seeds in soil. Something you did with your own hands. The ritual has already begun — before anything has grown.',
    Icon: PackageOpen,
    motionClass: styles.motionOpen,
  },
  {
    day: 'Day 3',
    label: 'Something Stirs',
    desc: 'Tiny shoots emerge overnight. Your windowsill is no longer just a windowsill. You\'ll find yourself checking in each morning.',
    Icon: Sprout,
    motionClass: styles.motionTime,
  },
  {
    day: 'Day 7',
    label: 'You Harvest',
    desc: 'Fresh food you grew yourself. Toss it on anything. Taste the difference — not just in flavor, but in how it feels to know exactly what you\'re eating.',
    Icon: Wheat,
    motionClass: styles.motionResult,
  },
];

export function StepsSection() {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const node = sectionRef.current;
      if (!node) return;

      const rect = node.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const start = viewportHeight * 0.82;
      const end = -rect.height * 0.2;
      const raw = (start - rect.top) / (start - end);
      const clamped = Math.max(0, Math.min(1, raw));

      setScrollProgress(clamped);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const progressPercent = useMemo(() => scrollProgress * 100, [scrollProgress]);
  const activeIndex = useMemo(() => {
    const index = Math.floor(scrollProgress * steps.length);
    return Math.min(index, steps.length - 1);
  }, [scrollProgress]);

  return (
    <Section id="ako-to-funguje" title="How the Ritual Unfolds">
      <Reveal>
        <p className={styles.subtitle}>
          Seven days. Three moments. One new habit that actually stays.
        </p>
      </Reveal>

      <div className={styles.timelineWrap} ref={sectionRef}>
        <div className={styles.lineTrack} aria-hidden="true">
          <div className={styles.lineFill} style={{ height: `${progressPercent}%` }} />
        </div>

        <ol className={styles.steps}>
          {steps.map((step, index) => {
            const isVisible = index <= activeIndex;

            return (
              <li
                key={step.day}
                className={`${styles.step} ${isVisible ? styles.stepVisible : ''}`.trim()}
                style={{ transitionDelay: `${index * 90}ms` }}
              >
                <span className={styles.pin} aria-hidden="true" />
                <article className={styles.card}>
                  <div className={styles.cardTop}>
                    <div>
                      <p className={styles.day}>{step.day}</p>
                      <h3 className={styles.stepTitle}>{step.label}</h3>
                    </div>
                    <div className={styles.iconWrap} aria-hidden="true">
                      <step.Icon className={`${styles.icon} ${step.motionClass}`.trim()} strokeWidth={1.5} />
                    </div>
                  </div>
                  <p className={styles.stepDesc}>{step.desc}</p>
                </article>
              </li>
            );
          })}
        </ol>
      </div>

      <Reveal>
        <p className={styles.hint}>Scroll slowly. Let it grow.</p>
      </Reveal>
    </Section>
  );
}
